import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Layers } from "lucide-react";
import StateSelector from "@/components/StateSelector";
import UserSelector from "@/components/UserSelector";
import ModalShell from "@/components/ModalShell";

const CATEGORIES = ["unassigned", "product", "strain", "marketing", "retail", "sales", "merch"];

export default function BulkAssignModal({ open, assets = [], onClose, onSaved }) {
  const [form, setForm] = useState({ category: "marketing", subcategory: "", tags: "", states: "", allowed_user_ids: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ category: "marketing", subcategory: "", tags: "", states: "", allowed_user_ids: "" });
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if ((form.states || "").trim() === "" && (form.allowed_user_ids || "").trim() === "") {
      toast.error("Add at least one location or user so assets can leave staging");
      return;
    }
    setBusy(true);
    try {
      const ids = assets.map((a) => a.id);
      await api.post("/admin/assets/bulk-assign", {
        ids,
        category: form.category,
        subcategory: form.subcategory,
        tags: form.tags,
        states: form.states,
        allowed_user_ids: form.allowed_user_ids,
      });
      toast.success(`Published ${ids.length} ${ids.length === 1 ? "asset" : "assets"} to ${form.category}`);
      onSaved && onSaved(ids);
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-xl" testId="bulk-assign-modal">
      <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm" data-testid="bulk-assign-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 flex items-center gap-2">
        <Layers className="h-3 w-3" /> Bulk Assign
      </div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight">
        Publish {assets.length} {assets.length === 1 ? "asset" : "assets"}
      </h2>
      <p className="text-xs text-muted-foreground mt-1">
        These values will be applied to every selected asset, then they&apos;ll leave the Media Manager and appear on their category page.
      </p>

      {assets.length > 0 && (
        <div className="mt-4 max-h-24 overflow-y-auto border border-border rounded-sm p-2 bg-background text-xs space-y-0.5" data-testid="bulk-assign-preview">
          {assets.slice(0, 8).map((a) => (
            <div key={a.id} className="truncate text-muted-foreground">• {a.title}</div>
          ))}
          {assets.length > 8 && (
            <div className="text-[11px] text-muted-foreground/70 italic">+ {assets.length - 8} more…</div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="bulk-assign-cat"
              className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subcategory (optional)">
            <input
              value={form.subcategory}
              onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
              data-testid="bulk-assign-sub"
              className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
            />
          </Field>
        </div>

        <Field label="Tags (comma-separated, optional)">
          <input
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            data-testid="bulk-assign-tags"
            className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
          />
        </Field>

        <StateSelector
          value={form.states}
          onChange={(str) => setForm({ ...form, states: str })}
          testIdPrefix="bulk-assign-state"
        />
        <UserSelector
          value={form.allowed_user_ids}
          onChange={(str) => setForm({ ...form, allowed_user_ids: str })}
          testIdPrefix="bulk-assign-users"
        />

        <button
          type="submit"
          disabled={busy || assets.length === 0}
          className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="bulk-assign-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Publish {assets.length} {assets.length === 1 ? "Asset" : "Assets"}
        </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">
        {label}
      </label>
      {children}
    </div>
  );
}
